import { useCallback, useEffect, useState } from 'react';

import type { DashboardDataModel } from '@/domain/models/dashboard.js';
import type { DashboardController } from '@/presentation/controllers/dashboard-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';

type State = { data: DashboardDataModel | null; isLoading: boolean; error: string | null };

export function useDashboard(controller: DashboardController) {
    const { translate, useProjectStore } = useApp();
    const selectedProject = useProjectStore((s) => s.selectedProject);
    const [state, setState] = useState<State>({ data: null, isLoading: true, error: null });

    const load = useCallback(
        async (isStale?: () => boolean) => {
            setState((s) => ({ ...s, isLoading: true, error: null }));
            try {
                const result = await controller.loadDashboard({ projectId: selectedProject?.id ?? undefined });
                if (isStale?.()) {
                    return;
                }
                result.fold(
                    (error) => setState({ data: null, isLoading: false, error: error.message }),
                    (data) => setState({ data, isLoading: false, error: null })
                );
            } catch {
                if (isStale?.()) {
                    return;
                }
                setState({ data: null, isLoading: false, error: translate('errors.loadError') });
            }
        },
        [controller, selectedProject, translate]
    );

    useEffect(() => {
        let stale = false;
        load(() => stale);
        return () => {
            stale = true;
        };
    }, [load]);

    const reload = useCallback(() => load(), [load]);

    return { ...state, reload };
}
